import { useCallback, useEffect, useState } from "react";
import { Tabs, Modal, message } from "antd";
import dayjs from "dayjs";
import {
  cancelCollabInvite,
  fetchUserActivities,
  reviewBusinessApplication,
} from "services/SauceService";
import { PersonalInviteStatusEnum } from "interfaces/creator";
import { useLocation, useNavigate } from "react-router-dom";
import ModalCollabTimeSlot from "pages/influencers/ModalCollabTimeSlot";
import { parseHourTo24 } from "pages/applications/Applications";
import { Venue } from "interfaces/venue";
import BookingsTab from "./BookingsTab";
import ApplicationsTab from "./ApplicationsTab";
import InvitationsTab from "./InvitationsTab";
import LoadingOverlay from "./LoadingOverlay";
import "./PendingActivities.scss";

export interface AddressCoordinates {
  type: "Point";
  coordinates: number[]; // [lng, lat]
}

export interface VenueWorkingHour {
  _id?: string;
  dayOfWeek?: string;
  startTime?: string;
  endTime?: string;
  open?: boolean;
}

export interface RequestDate {
  _id?: string;
  date?: string | null;
  hour?: string | null;
}

export interface ApplicationDate {
  hour?: string | null;
  date?: string | null;
}

export interface PendingActivity {
  collabId?: string | null;
  bookingId?: string | null;
  inviteId?: string | null;
  inviteStatus?: PersonalInviteStatusEnum | null;
  venue: Venue;
  startDate?: string | null;
  endDate?: string | null;
  status?: number | null;
  bookingStatus?: number | null;
  foodieId?: string;
  username?: string;
  instagramFollowersCount?: number;
  tiktokFollowersCount?: number;
  collabMatchRate?: number | null;
  selfManagedCompensationEnabled?: boolean;
  deliveryTeamApproval?: boolean;
  avatarUrl?: string | null;
  isPremium?: boolean;
  top3PostsCoverImgUrl?: string[];
  dinnerIntentByPosts?: any;
  commitmentRate?: number;
  engagementRate?: number | null;
  deliveryAddress?: string | null;
  requestDates?: RequestDate[];
  applicationDate?: ApplicationDate;
  dueDateToReview?: string | null;
  tabStatus?: string;
  securityCode?: string | null;
  foodCompAmount?: number | null;
  cashCompAmount?: number | null;
  guestAllowed?: boolean;
  name?: string;
  verified?: boolean;
  requestedAt?: string | null;
  [key: string]: any;
}

interface PendingActivitiesProps {
  creator?: PendingActivity;
  showCreatorInfo?: boolean;
}

export const formatLeft = (details: string) => {
  return details || "";
};

export const computeTimeLeft = (expiresAt?: string | number) => {
  if (!expiresAt) return "";
  const now = dayjs();
  const end = dayjs(expiresAt);
  if (!end.isValid() || end.isBefore(now)) return "Expiring soon";
  const diff = end.diff(now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (days > 0) return `${days}d ${hours}h left`;
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
};

const getBookingTimestamp = (item: any) => {
  const date = item?.applicationDate?.date;
  const hour = item?.applicationDate?.hour;
  if (!date) return 0;

  const timeStr = parseHourTo24(hour);
  const datePart = date.includes("T") ? date.split("T")[0] : date;

  const dateTimeStr = hour ? `${datePart}T${timeStr}Z` : date;
  const ts = new Date(dateTimeStr).getTime();
  return isNaN(ts) ? 0 : ts;
};

export const canRescheduleAccepted = (influencer: any) => {
  if (!influencer) return false;
  const status = String(
    influencer?.tabStatus ?? influencer?.TabStatus ?? ""
  ).toLowerCase();
  if (status !== "accepted") return false;

  const app = influencer?.applicationDate;
  if (!app) return false;

  const baseDate = app.date ? dayjs(app.date) : null;
  if (!baseDate || !baseDate.isValid()) return false;

  let scheduled = baseDate;
  if (app.hour) {
    const parsedTime = dayjs(app.hour, "h:mm A");
    if (parsedTime.isValid()) {
      scheduled = scheduled
        .hour(parsedTime.hour())
        .minute(parsedTime.minute())
        .second(0)
        .millisecond(0);
    }
  }

  return scheduled.isAfter(dayjs().add(3, "hour"));
};

const PendingActivities: React.FC<PendingActivitiesProps> = ({
  creator,
  showCreatorInfo,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activities, setActivities] = useState<any>({
    bookings: [],
    applications: [],
    invitations: [],
    content: [],
  });

  const [openModalTimeSlot, setOpenModalTimeSlot] = useState(false);
  const [selectedInfluencer, setSelectedInfluencer] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("upcoming");

  const bookings = activities.bookings || [];
  const applications = activities.applications || [];

  const onClickCancelInvite = (invite: PendingActivity) => {
    Modal.confirm({
      content: "Are you sure you want to cancel this invite?",
      icon: "",
      onOk: async () => {
        const params = {
          inviteId: invite.inviteId,
        };
        await cancelCollabInvite(params);
        message.success("Invite cancelled");
        await getCreatorActivities();
      },
      okButtonProps: {
        type: "primary",
      },
    });
  };

  const onClickAccept = (evt: any, influencer: any) => {
    evt.stopPropagation();
    setSelectedInfluencer(influencer);
    setOpenModalTimeSlot(true);
  };

  const onClickReject = async (evt: any, influencer: any) => {
    evt.stopPropagation();

    Modal.confirm({
      content: "Are you sure you want to reject this influencer?",
      icon: "",
      onOk: async () => {
        await reviewBusinessApplication({
          bookingId: influencer?.bookingId,
          collabId: influencer?.collabId,
          status: "reject",
        });

        await getCreatorActivities();
        navigate(location.pathname, { replace: true });
      },
      okButtonProps: {
        type: "primary",
      },
    });
  };

  const getCreatorActivities = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        businessId: localStorage.getItem("businessId") || "",
        foodieId: creator?.foodieId,
      };

      const response = await fetchUserActivities(params);

      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : [];
      const grouped = {
        bookings: [] as any[],
        applications: [] as any[],
        invitations: [] as any[],
        content: [] as any[],
      };

      list.forEach((item: any) => {
        const raw = item.TabStatus ?? item.tabStatus ?? item.tabstatus ?? "";
        const status = String(raw).toLowerCase().trim();

        if (status === "accepted") {
          grouped.bookings.push(item);
        } else if (status === "pending") {
          grouped.applications.push(item);
        } else if (status === "invited") {
          grouped.invitations.push(item);
        } else if (status === "content") {
          grouped.content.push(item);
        } else {
          if (item.type === "booking") grouped.bookings.push(item);
          else if (item.type === "content") grouped.content.push(item);
          else grouped.applications.push(item);
        }
      });

      grouped.bookings.sort(
        (a: any, b: any) => getBookingTimestamp(a) - getBookingTimestamp(b)
      );

      grouped.applications.sort(
        (a: any, b: any) =>
          new Date(a.requestedAt).getTime() - new Date(b.requestedAt).getTime()
      );

      setActivities(grouped);
    } catch (err) {
      console.error("Failed to organize activities by TabStatus", err);
    } finally {
      setLoading(false);
    }
  }, [creator?.foodieId]);

  useEffect(() => {
    getCreatorActivities();
  }, [getCreatorActivities]);

  return (
    <div className="pending-activities-wrapper">
      <ModalCollabTimeSlot
        open={openModalTimeSlot}
        onCancel={() => {
          setOpenModalTimeSlot(false);
          setSelectedInfluencer(null);
          getCreatorActivities();
        }}
        requestDates={selectedInfluencer?.requestDates}
        influencer={selectedInfluencer}
      />

      <div className="pa-card" style={{ position: "relative" }}>
        <LoadingOverlay loading={loading} />

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane tab="Bookings" key="upcoming">
            <BookingsTab
              bookings={bookings}
              loading={loading}
              showCreatorInfo={showCreatorInfo}
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab="Applications" key="applications">
            <ApplicationsTab
              applications={applications}
              loading={loading}
              showCreatorInfo={showCreatorInfo}
              onAccept={onClickAccept}
              onReject={onClickReject}
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab="Invitations" key="invitations">
            <InvitationsTab
              showCreatorInfo={showCreatorInfo}
              onCancel={onClickCancelInvite}
              isActive={activeTab === "invitations"}
              onRefresh={getCreatorActivities}
              creatorId={creator?.foodieId}
            />
          </Tabs.TabPane>
        </Tabs>
      </div>
    </div>
  );
};

export default PendingActivities;
